import React, { createContext, useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

/**
 * SidebarContext - Manages layout state for the UnifiedSidebar architecture.
 *
 * Desktop/Tablet (>=600px):
 *   UnifiedSidebar: permanent drawer, toggleable between expanded (280px) and
 *   collapsed (48px icon-only) modes. Visible on ALL routes (like ChatGPT).
 *
 * Phone (<600px):
 *   MobileHeader (top) + thread list as overlay drawer
 *
 * States:
 *   - isSidebarExpanded: boolean (desktop/tablet) — persisted in localStorage
 *   - isOverlayOpen: boolean (mobile thread list drawer)
 */

const SidebarContext = createContext(null);

// Routes where sidebar nav items show as "inactive"
const NON_CHAT_ROUTES = ['/editor', '/editor/plans', '/collections', '/explore', '/agents', '/memory'];

export const SidebarProvider = ({ children }) => {
  const muiTheme = useMuiTheme();
  const location = useLocation();

  // Breakpoints
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));        // <600px
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md')); // 600-899px
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));          // >=900px

  // Derived: is current route a chat route?
  const isChatRoute = !NON_CHAT_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // ── Primary sidebar state ──────────────────────────────────────────────────
  // Migration-safe init: prefer new key, fall back to legacy key
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem('sidebarExpanded')
      ?? localStorage.getItem('threadPanelOpen');
    return saved === null ? true : saved === 'true';
  });

  // Mobile overlay state (thread list drawer)
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Search modal state (shared between sidebar click + Cmd+K)
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Close mobile overlay when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsOverlayOpen(false);
    }
  }, [isMobile]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const toggleSidebar = useCallback(() => {
    setIsSidebarExpanded(prev => {
      const next = !prev;
      localStorage.setItem('sidebarExpanded', String(next));
      return next;
    });
  }, []);

  const openSidebar = useCallback(() => {
    setIsSidebarExpanded(true);
    localStorage.setItem('sidebarExpanded', 'true');
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarExpanded(false);
    localStorage.setItem('sidebarExpanded', 'false');
  }, []);

  const openOverlay = useCallback(() => setIsOverlayOpen(true), []);
  const closeOverlay = useCallback(() => setIsOverlayOpen(false), []);

  const openSearch = useCallback(() => setIsSearchOpen(true), []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);

  // Ctrl+B / Cmd+B shortcut
  const handleToggleShortcut = useCallback(() => {
    if (isMobile) {
      setIsOverlayOpen(prev => !prev);
    } else {
      toggleSidebar();
    }
  }, [isMobile, toggleSidebar]);

  // ── Computed ───────────────────────────────────────────────────────────────
  const showSidebar = !isMobile && isSidebarExpanded;

  const value = useMemo(() => ({
    // Breakpoints
    isMobile,
    isTablet,
    isDesktop,

    // Route
    isChatRoute,

    // Sidebar state
    isSidebarExpanded,
    showSidebar,
    toggleSidebar,
    openSidebar,
    closeSidebar,

    // Mobile overlay
    isOverlayOpen,
    openOverlay,
    closeOverlay,

    // Search modal
    isSearchOpen,
    openSearch,
    closeSearch,

    // Shortcut handler
    handleToggleShortcut,
  }), [
    isMobile,
    isTablet,
    isDesktop,
    isChatRoute,
    isSidebarExpanded,
    showSidebar,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    isOverlayOpen,
    openOverlay,
    closeOverlay,
    isSearchOpen,
    openSearch,
    closeSearch,
    handleToggleShortcut,
  ]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === null) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext;
